/**
 * Dropdown positioning utilities for vue-multiselect.
 *
 * vue-multiselect renders its list inline, which gets clipped by
 * overflow:hidden/auto ancestors (modals, panels). These utilities apply
 * position:fixed to escape that, and teleport the list to <body> when a CSS
 * transform ancestor would otherwise confine position:fixed to its own
 * containing block (e.g. reka-ui portals).
 */

export function getFixedContainingBlock(el) {
  let parent = el.parentElement
  while (parent) {
    const { transform, willChange } = window.getComputedStyle(parent)
    const hasTransform = transform && transform !== 'none'
    const hasWillChangeTransform =
      willChange &&
      (willChange.includes('transform') || willChange.includes('perspective'))
    if (hasTransform || hasWillChangeTransform) return parent
    parent = parent.parentElement
  }
  return null
}

export function getScrollableAncestors(el) {
  const ancestors = []
  let parent = el.parentElement
  while (parent) {
    const { overflow, overflowX, overflowY } = window.getComputedStyle(parent)
    if (/(auto|scroll)/.test(overflow + overflowX + overflowY)) {
      ancestors.push(parent)
    }
    parent = parent.parentElement
  }
  return ancestors
}

export function teleportListToBody(list, triggerEl) {
  if (list._teleportedToBody) return
  list._teleportOriginalParent = list.parentElement
  list._teleportOriginalNextSibling = list.nextSibling

  const { fontSize, fontFamily } = window.getComputedStyle(triggerEl)
  list.style.fontSize = fontSize
  list.style.fontFamily = fontFamily

  // Stop mousedown/pointerdown from reaching vue-multiselect's and
  // reka-ui's document-level outside-click/dismiss handlers.
  const stop = (e) => e.stopPropagation()
  list._stopEvents = stop
  list.addEventListener('mousedown', stop)
  list.addEventListener('pointerdown', stop)

  document.body.appendChild(list)
  list._teleportedToBody = true
}

export function restoreListFromBody(list) {
  if (!list._teleportedToBody) return

  if (list._stopEvents) {
    list.removeEventListener('mousedown', list._stopEvents)
    list.removeEventListener('pointerdown', list._stopEvents)
    list._stopEvents = null
  }

  const parent = list._teleportOriginalParent
  const next = list._teleportOriginalNextSibling
  if (parent?.isConnected) {
    parent.insertBefore(list, next || null)
  } else {
    list.remove() // parent gone (unmounting) — avoid orphans
  }
  list._teleportedToBody = false
  list._teleportOriginalParent = null
  list._teleportOriginalNextSibling = null
}

/**
 * @param {Element} msEl   - the multiselect root element ($el)
 * @param {Element} list   - the dropdown list element ($refs.list)
 * @param {boolean} instant - skip transition on first paint
 */
export function positionDropdownList(msEl, list, instant = false) {
  if (!list || !msEl?.classList.contains('multiselect--active')) return

  if (instant) list.style.transition = 'none'

  if (getFixedContainingBlock(msEl)) {
    teleportListToBody(list, msEl)
  }

  const { top, bottom, left } = msEl.getBoundingClientRect()
  const showAbove = msEl.classList.contains('multiselect--above')

  list.style.position = 'fixed'
  list.style.setProperty('z-index', 'var(--z-dropdown)')
  list.style.width = `${msEl.clientWidth}px`
  list.style.bottom = 'auto'
  list.style.left = `${left}px`
  list.style.top = showAbove ? `${top - list.offsetHeight}px` : `${bottom}px`

  if (instant) {
    list.offsetHeight // force layout before restoring transitions
    list.style.transition = 'top 0.12s ease, left 0.12s ease'
  }
}

export function animateOpen(msEl, list) {
  list.classList.toggle(
    'is-above',
    msEl.classList.contains('multiselect--above'),
  )
  list.classList.add('is-opening')
  list.addEventListener(
    'animationend',
    () => list.classList.remove('is-opening'),
    { once: true },
  )
}

export function animateClose(list) {
  list.classList.add('is-closing')
  let cleaned = false
  const cleanup = () => {
    if (cleaned) return
    cleaned = true
    list.classList.remove('is-closing', 'is-above')
    restoreListFromBody(list)
  }
  list.addEventListener('animationend', cleanup, { once: true })
  setTimeout(cleanup, 200) // fallback if animationend never fires
}
